import { type UserEventData } from '@moniebook-web/utils';
import Dexie, { type Table } from 'dexie';
import { DB_MIGRATION_LOG_EVENT } from 'src/types';
import { logger } from '../log';
import type {
    Category,
    Discount,
    Docket,
    Item,
    ItemVariant,
    ItemVariantComposition,
    MeasurementUnit,
    Modifier,
    PaymentMethod,
    PaymentRestriction,
    PricingGroup,
    Printer,
    RemoteCart,
    Staff,
    Tax,
    Terminal,
    UnsyncedSale,
} from '../models';
import { runBarcodeMigrationInBackground } from '../services/barcode-migration';
import { useBusinessStore } from '../store';
import {
    schemaV10,
    schemaV11,
    schemaV12,
    schemaV13,
    schemaV14,
    schemaV15,
    schemaV16,
    schemaV17,
    schemaV18,
    schemaV19,
    schemaV2,
    schemav20,
    schemaV21,
    schemaV22,
    schemaV23,
    schemaV24,
    schemaV25,
    schemaV26,
    schemaV27,
    schemaV28,
    schemaV29,
    schemaV3,
    schemaV30,
    schemaV31,
    schemaV32,
    schemaV33,
    schemaV34,
    schemaV35,
    schemaV4,
    schemaV5,
    schemaV6,
    schemaV7,
    schemaV8,
    schemaV9,
    v14Upgrade,
    v16Upgrade,
    v17Upgrade,
    v19Upgrade,
    v27Upgrade,
    v28Upgrade,
    v29Upgrade,
    v30Upgrade,
    v3Upgrade,
    v8Upgrade,
} from './schema';
import { requestPersistentStorage } from './utils';

export class AppDatabase extends Dexie {
    constructor() {
        super('moniebookDB');
        this.version(2).stores(schemaV2);
        this.version(3).stores(schemaV3).upgrade(v3Upgrade);
        this.version(4).stores(schemaV4);
        this.version(5).stores(schemaV5);
        this.version(6).stores(schemaV6);
        this.version(7).stores(schemaV7);
        this.version(8).stores(schemaV8).upgrade(v8Upgrade);
        this.version(9).stores(schemaV9);
        this.version(10).stores(schemaV10);
        this.version(11).stores(schemaV11);
        this.version(12).stores(schemaV12);
        this.version(13).stores(schemaV13);
        this.version(14).stores(schemaV14).upgrade(v14Upgrade);
        this.version(15).stores(schemaV15);
        this.version(16).stores(schemaV16).upgrade(v16Upgrade);
        this.version(17).stores(schemaV17).upgrade(v17Upgrade);
        this.version(18).stores(schemaV18);
        this.version(19).stores(schemaV19).upgrade(v19Upgrade);
        this.version(20).stores(schemav20);
        this.version(21).stores(schemaV21);
        this.version(22).stores(schemaV22);
        this.version(23).stores(schemaV23);
        this.version(24).stores(schemaV24);
        this.version(25).stores(schemaV25);
        this.version(26).stores(schemaV26);
        this.version(27).stores(schemaV27).upgrade(v27Upgrade);
        this.version(28).stores(schemaV28).upgrade(v28Upgrade);
        this.version(29).stores(schemaV29).upgrade(v29Upgrade);
        this.version(30).stores(schemaV30).upgrade(v30Upgrade);
        this.version(31).stores(schemaV31);
        this.version(32).stores(schemaV32);
        this.version(33).stores(schemaV33);
        this.version(34).stores(schemaV34);
        this.version(35).stores(schemaV35);

        this.on('ready', () => {
            requestPersistentStorage();
            runBarcodeMigrationInBackground(this).catch((err) => {
                const { businessInfo } = useBusinessStore.getState();

                logger.error({
                    eventName: DB_MIGRATION_LOG_EVENT,
                    message: `v34 failed', ${JSON.stringify({
                        err,
                        businessId: businessInfo.businessId,
                        branchId: businessInfo.branchId,
                    })}`,
                });
            });
        });
    }

    taxes!: Table<Tax>;
    paymentMethods!: Table<PaymentMethod>;
    staff!: Table<Staff>;

    items!: Table<Item>;
    itemVariants!: Table<ItemVariant>;
    itemVariantCompositions!: Table<ItemVariantComposition>;

    measurementUnits!: Table<MeasurementUnit>;
    categories!: Table<Category>;

    discounts!: Table<Discount>;
    unsyncedSales!: Table<UnsyncedSale>;

    carts!: Table<RemoteCart>;
    terminals!: Table<Terminal>;

    pricingGroups!: Table<PricingGroup>;
    mixpanelEvents!: Table<UserEventData>;
    paymentRestrictions!: Table<PaymentRestriction>;

    modifiers!: Table<Modifier>;

    printers!: Table<Printer>;

    dockets!: Table<Docket>;
}

export const db = new AppDatabase();
